import { Component, ElementRef, HostBinding, Input, OnDestroy, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { FADE_REVEAL } from '@app/chat/atimations';
import { DocumentExtended } from '@app/models/document-extended';
import { DocumentService } from '@app/modules/reusable/documents/services/document.service';
import { EXPAND_COLLAPSE_ANIMATION } from '@app/shared/animations';
import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';

/**
 * Селект папки у вигляді дерева.
 *
 * Бек віддає всі рівні одним запитом (groups[]=document:children), тому
 * підвантаження по рівнях не потрібне — просто рекурсивно малюємо children.
 * З цієї ж причини пошук локальний: усі дані вже на клієнті.
 *
 * Керується через [control], як app-multiselect-checkbox: значення — id папки.
 */
@Component({
  selector: 'app-folder-tree-select',
  templateUrl: './folder-tree-select.component.html',
  styleUrls: ['./folder-tree-select.component.scss'],
  animations: [FADE_REVEAL, EXPAND_COLLAPSE_ANIMATION],
  standalone: false
})
export class FolderTreeSelectComponent implements OnDestroy {
  @Input() control: FormControl;
  @Input() placeholder = '';

  @Input() excludeIds: number[] = [];
  @HostBinding('class.folder-tree--disabled') @Input() disabled = false;

  /** Скоуп дерева. Проєкт має пріоритет над контактом. */
  @Input() set contactId(value: number) {
    if (value !== this._contactId) {
      this._contactId = value;
      this.resetTree();
    }
  }

  @Input() set matterId(value: number) {
    if (value !== this._matterId) {
      this._matterId = value;
      this.resetTree();
    }
  }

  /** Ширина дропдауна = ширині поля; CDK-оверлей сам її не успадковує */
  panelWidth: number = null;

  loading = false;
  tree: DocumentExtended[] = [];
  /** Дерево після локального пошуку — саме воно рендериться */
  visibleTree: DocumentExtended[] = [];

  pendingId: number = null;
  /**
   * Значення на момент відкриття. Використовується двічі: до нього
   * повертає «Скасувати», і саме ця папка позначається як поточна —
   * позначка не переїжджає слідом за новим вибором.
   */
  initialId: number = null;

  private _contactId: number;
  private _matterId: number;
  private _search = '';
  private loaded = false;
  private prefetchScheduled = false;
  /** Розгорнуті вузли. За замовчуванням видно тільки верхній рівень. */
  private expanded = new Set<number>();
  private unsubscribe$ = new Subject<void>();

  @ViewChild('fieldEl') private fieldEl: ElementRef<HTMLElement>;

  constructor(private documentService: DocumentService) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  get search(): string {
    return this._search;
  }

  set search(value: string) {
    this._search = value ?? '';
    this.applySearch();
  }

  get selectedName(): string {
    const id = this.control?.value;
    return id ? this.findNode(this.tree, +id)?.name ?? '' : '';
  }

  onTriggerClick(): void {
    this.panelWidth = this.fieldEl?.nativeElement?.offsetWidth ?? null;
    this.search = '';
    this.pendingId = this.control?.value ? +this.control.value : null;
    this.initialId = this.pendingId;
    this.expandToPending();
    this.loadTree();
  }

  /** Під час пошуку показуємо все знайдене розгорнутим */
  isExpanded(node: DocumentExtended): boolean {
    return !!this._search || this.expanded.has(node.id);
  }

  select(node: DocumentExtended): void {
    this.pendingId = this.pendingId === node.id ? null : node.id;
    this.apply();

    if (this.childrenOf(node).length) {
      if (this.expanded.has(node.id)) {
        this.expanded.delete(node.id);
      } else {
        this.expanded.add(node.id);
      }
    }
  }

  apply(): void {
    this.control?.setValue(this.pendingId);
  }

  cancel(): void {
    this.pendingId = this.initialId;
    this.apply();
  }

  /** Показуємо все, що прийшло з беку — фільтрація на його боці */
  childrenOf(node: DocumentExtended): DocumentExtended[] {
    return node?.children ?? [];
  }

  private resetTree(): void {
    this.loaded = false;
    this.tree = [];
    this.visibleTree = [];
    this.expanded.clear();
    this.prefetchTree();
  }

  private prefetchTree(): void {
    if (this.prefetchScheduled) {
      return;
    }

    this.prefetchScheduled = true;

    Promise.resolve().then(() => {
      this.prefetchScheduled = false;

      if (!this.disabled) {
        this.loadTree();
      }
    });
  }

  private loadTree(): void {
    if (this.loaded || this.loading) {
      return;
    }

    this.loading = true;
    this.documentService
      .getFolderTree(this._contactId, this._matterId)
      .pipe(
        take(1),
        finalize(() => (this.loading = false)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: folders => {
          this.tree = this.pruneExcluded(folders ?? []);
          this.loaded = true;
          // Гілку до вибраної папки розкриваємо вже маючи дані
          this.expandToPending();
          this.applySearch();
        },
        error: () => {
          this.tree = [];
          this.visibleTree = [];
        }
      });
  }

  private pruneExcluded(nodes: DocumentExtended[]): DocumentExtended[] {
    if (!this.excludeIds?.length) {
      return nodes;
    }

    return (nodes ?? [])
      .filter(node => !this.excludeIds.includes(node.id))
      .map(node => ({ ...node, children: this.pruneExcluded(this.childrenOf(node)) }));
  }

  private applySearch(): void {
    const query = this._search.trim().toLowerCase();
    this.visibleTree = query ? this.filterNodes(this.tree, query) : this.tree;
  }

  private filterNodes(nodes: DocumentExtended[], query: string): DocumentExtended[] {
    const result: DocumentExtended[] = [];

    for (const node of nodes ?? []) {
      const children = this.filterNodes(this.childrenOf(node), query);
      const matches = (node.name ?? '').toLowerCase().includes(query);

      if (matches || children.length) {
        result.push({ ...node, children });
      }
    }

    return result;
  }

  /** Розкриває всі рівні від кореня до вибраної папки включно */
  private expandToPending(): void {
    if (!this.pendingId || !this.tree.length) {
      return;
    }

    const path = this.findPath(this.tree, this.pendingId);
    path.forEach(node => this.expanded.add(node.id));
  }

  private findPath(nodes: DocumentExtended[], id: number): DocumentExtended[] {
    for (const node of nodes ?? []) {
      if (node.id === id) {
        return [node];
      }

      const childPath = this.findPath(this.childrenOf(node), id);
      if (childPath.length) {
        return [node, ...childPath];
      }
    }

    return [];
  }

  private findNode(nodes: DocumentExtended[], id: number): DocumentExtended {
    for (const node of nodes ?? []) {
      if (node.id === id) {
        return node;
      }

      const found = this.findNode(this.childrenOf(node), id);
      if (found) {
        return found;
      }
    }

    return null;
  }
}
